import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getRoles, syncUserRoles } from "../../api/users";
import { Role } from "../../types/user";
import { useNotification } from "../../hooks/useNotification";

interface UserRolesModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number;
    userName: string;
    currentRoles: string[];
    onRolesUpdated: () => void;
}

const UserRolesModal: React.FC<UserRolesModalProps> = ({
    isOpen,
    onClose,
    userId,
    userName,
    currentRoles,
    onRolesUpdated,
}) => {
    const { t } = useTranslation("users");
    const { t: tCommon } = useTranslation("common");
    const { showNotification } = useNotification();
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingRoles, setIsLoadingRoles] = useState(false);

    useEffect(() => {
        const loadAvailableRoles = async () => {
            setIsLoadingRoles(true);
            try {
                const roles = await getRoles();
                setAvailableRoles(roles);
            } catch {
                showNotification(tCommon("error_loading_roles"), "error");
            } finally {
                setIsLoadingRoles(false);
            }
        };

        if (isOpen) {
            setSelectedRoles([...currentRoles]);
            loadAvailableRoles();
        }
    }, [isOpen, currentRoles, showNotification, tCommon]);

    const handleRoleToggle = (roleName: string) => {
        setSelectedRoles((prev) =>
            prev.includes(roleName)
                ? prev.filter((role) => role !== roleName)
                : [...prev, roleName]
        );
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            await syncUserRoles(userId, selectedRoles);
            showNotification(t("users.roles_updated_successfully"), "success");
            onRolesUpdated();
            onClose();
        } catch {
            showNotification(tCommon("error_updating_roles"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setSelectedRoles([...currentRoles]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t("edit_roles_for")} {userName}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {isLoadingRoles ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="max-h-64 overflow-y-auto">
                            {availableRoles.map((role) => (
                                <label
                                    key={role.id}
                                    className="flex items-center space-x-3 p-2 hover:bg-gray-800 dark:bg-gray-900 rounded"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedRoles.includes(
                                            role.name
                                        )}
                                        onChange={() =>
                                            handleRoleToggle(role.name)
                                        }
                                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="flex-1">
                                        <span className="font-medium text-gray-600 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-300">
                                            {role.name}
                                        </span>
                                        <span className="text-sm text-gray-500 dark:text-gray-100 ml-2 hover:text-gray-700 dark:hover:text-gray-300">
                                            ({role.guard_name})
                                        </span>
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t">
                            <button
                                onClick={handleCancel}
                                disabled={isLoading}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50"
                            >
                                {tCommon("cancel")}
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        {tCommon("saving")}
                                    </div>
                                ) : (
                                    tCommon("save")
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserRolesModal;
